import React, { useEffect, useState } from "react";
import { HStack, Select, useToast, Button, FormControl } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import fetchWithToken from "views/auth/signIn/axiosInstance";
import { MdOutlineFilterAltOff } from "react-icons/md";

function getToday() {
  const d = new Date();
  return d.toISOString().slice(0, 10);
}
function getTomorrow() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
}

export default function Filters({
  dateFilter, setDateFilter,
  branch, setBranch,
  companyId: propCompanyId,
  setCompanyId,
  customRange, setCustomRange,
  filterStatus, setFilterStatus,
  ordersPerPage, setOrdersPerPage,
  currentPage, setCurrentPage,
  selectedService: controlledSelectedService,
  setSelectedService: controlledSetSelectedService,
  showCompanyFilter = true,
  showDateFilter = true,
  showBranchFilter = true,
  showServiceFilter = true,
  showStatusFilter = true,
  showOrdersPerPage = true
}) {
  const [branches, setBranches] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [branchServices, setBranchServices] = useState([]);
  const [loadingBranches, setLoadingBranches] = useState(false);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [loadingServices, setLoadingServices] = useState(false);
  const toast = useToast();
  const { t } = useTranslation();

  // Controlled/uncontrolled service state support
  const [uncontrolledSelectedService, setUncontrolledSelectedService] = useState("");
  const selectedService =
    controlledSelectedService !== undefined ? controlledSelectedService : uncontrolledSelectedService;
  const setSelectedService =
    controlledSetSelectedService !== undefined ? controlledSetSelectedService : setUncontrolledSelectedService;

  const companyId = propCompanyId;
  const safeCustomRange = customRange || { from: "", to: "" };

  /** ---------------- Fetch Companies ---------------- */
  useEffect(() => {
    if (!showCompanyFilter) return;
    setLoadingCompanies(true);
    const fetchCompanies = async () => {
      try {
        const url = `${process.env.REACT_APP_API_URL2}/companies/all?page_id=10`;
        const data = await fetchWithToken(url, "GET");
        if (data.status === "error") throw new Error(data.message || "Error fetching companies");
        setCompanies(data.data);
      } catch (error) {
        toast({
          title: t("Error"),
          description: error.message,
          status: "error",
          position: "top-right",
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setLoadingCompanies(false);
      }
    };
    fetchCompanies();
  }, [showCompanyFilter, t, toast]);

  /** ---------------- Fetch Branches ---------------- */
  useEffect(() => {
    if (!companyId) {
      setBranches([]);
      return;
    }
    setLoadingBranches(true);
    const fetchBranches = async () => {
      try {
        if (!companyId || companyId === "all") {
          setBranches([]);
          setBranch("all");
          return;
        }
        const url = `${process.env.REACT_APP_API_URL2}/branches?company_id=${companyId}&page_id=10`;
        const data = await fetchWithToken(url, "GET");
        if (data.status === "error") throw new Error(data.message || "Error fetching branches");
        setBranches(data.data);
        if (branch !== "all" && !data.data.some(b => String(b.id) === String(branch))) {
          setBranch("all");
        }
      } catch (error) {
        toast({
          title: t("Error"),
          description: error.message,
          status: "error",
          position: "top-right",
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setLoadingBranches(false);
      }
    };
    fetchBranches();
    // eslint-disable-next-line
  }, [companyId]);

  /** ---------------- Fetch Services for Branch ---------------- */
  useEffect(() => {
    if (!branch || branch === "all") {
      setBranchServices([]);
      setSelectedService("");
      return;
    }
    const fetchBranchServices = async () => {
      setLoadingServices(true);
      try {
        const url = `${process.env.REACT_APP_API_URL2}/branches/services?branch_id=${branch}&page_id=10`;
        const data = await fetchWithToken(url, "GET");
        if (data.status === "error") throw new Error(data.message || "Error fetching branch services");
        setBranchServices(data.data);
      } catch (error) {
        toast({
          title: t("Error"),
          description: error.message,
          status: "error",
          position: "top-right",
          duration: 5000,
          isClosable: true,
        });
        setBranchServices([]);
      } finally {
        setLoadingServices(false);
      }
    };
    fetchBranchServices();
  }, [branch, setSelectedService, toast, t]);

  /** ---------------- Date Logic ---------------- */
  let from = "";
  let to = "";
  if (dateFilter === "today") from = to = getToday();
  else if (dateFilter === "tomorrow") from = to = getTomorrow();
  else if (dateFilter === "custom") {
    from = safeCustomRange.from;
    to = safeCustomRange.to;
  }

  /** ---------------- Reset ---------------- */
  const handleReset = () => {
    setCompanyId && setCompanyId("");
    setBranch && setBranch("all");
    setDateFilter && setDateFilter("today");
    if (setFilterStatus) setFilterStatus("");
    setCustomRange && setCustomRange({ from: "", to: "" });
    setSelectedService && setSelectedService("");
  };

  return (
    
<HStack spacing={4} wrap="nowrap">
      {/* Company Filter */}
      {showCompanyFilter && (
        <Select
          value={companyId === undefined || companyId === null ? "all" : companyId}
          onChange={(e) => {
            const val = e.target.value;
            if (setCompanyId) setCompanyId(val === "all" ? "" : val);
            if (setBranch) setBranch("all");
          }}
          maxW="200px"
          isDisabled={loadingCompanies}
          focusBorderColor="blue.400"
          _hover={{
            borderColor: "blue.400",
            boxShadow: "0 0 0 1px #3182CE",
            color: "blue.400",
            backgroundColor: "blue.50",
          }}
        >
          <option value="all">{t("All Companies")}</option>
          {loadingCompanies ? (
            <option disabled>{t("Loading...")}</option>
          ) : (
            companies.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name || `Company ${c.id}`}
              </option>
            ))
          )}
        </Select>
      )}

     

      {/* Branch Filter */}
      {showBranchFilter && (
        <Select
          value={branch}
          onChange={(e) => setBranch(e.target.value)}
          maxW="180px"
          isDisabled={!companyId || loadingBranches}
          focusBorderColor="blue.400"
          _hover={{
            borderColor: "blue.400",
            boxShadow: "0 0 0 1px #3182CE",
            color: "blue.400",
            backgroundColor: "blue.50",
          }}
        >
          <option value="all">{t("All Branches")}</option>
          {loadingBranches ? (
            <option disabled>{t("Loading...")}</option>
          ) : (
            branches.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name || `Branch ${b.id}`}
              </option>
            ))
          )}
        </Select>
      )}

      {/* Service Filter */}
      {showServiceFilter && (
        <FormControl maxW="180px">
          <Select
            placeholder={t("Select Service")}
            value={selectedService}
            onChange={(e) => setSelectedService(e.target.value)}
            isDisabled={!branch || branch === "all" || loadingServices}
            focusBorderColor="blue.400"
            _hover={{
              borderColor: "blue.400",
              boxShadow: "0 0 0 1px #3182CE",
              color: "blue.400",
              backgroundColor: "blue.50",
            }}
          >
            {loadingServices ? (
              <option disabled>{t("Loading...")}</option>
            ) : (
              branchServices.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.service_name || `Service ${s.id}`}
                </option>
              ))
            )}
          </Select>
        </FormControl>
      )}
     {/* Date Filter */}
      {showDateFilter && (
        <>
          <Select
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            maxW="160px"
            focusBorderColor="blue.400"
            _hover={{
              borderColor: "blue.400",
              boxShadow: "0 0 0 1px #3182CE",
              color: "blue.400",
              backgroundColor: "blue.50",
            }}
          >
            <option value="today">{t("Today")}</option>
            <option value="tomorrow">{t("Tomorrow")}</option>
            <option value="after_tomorrow">{t("After Tomorrow")}</option>
            <option value="custom">{t("Custom")}</option>
          </Select>
          {dateFilter === "custom" && (
            <>
              <input
                type="date"
                value={safeCustomRange.from}
                onChange={(e) =>
                  setCustomRange((r) => ({ ...(r || { from: "", to: "" }), from: e.target.value }))
                }
                style={{
                  border: "1px solid #ccc",
                  borderRadius: 4,
                  padding: 6,
                  marginRight: 8,
                }}
              />
              <input
                type="date"
                value={safeCustomRange.to}
                onChange={(e) =>
                  setCustomRange((r) => ({ ...(r || { from: "", to: "" }), to: e.target.value }))
                }
                style={{ border: "1px solid #ccc", borderRadius: 4, padding: 6 }}
              />
            </>
          )}
        </>
      )}
      {/* Status Filter */}
      {showStatusFilter && (
        <FormControl maxW="160px">
          <Select
            value={filterStatus}
            onChange={(e) => {
              setFilterStatus && setFilterStatus(e.target.value);
              setCurrentPage && setCurrentPage(1);
            }}
            focusBorderColor="blue.400"
            _hover={{
              borderColor: "blue.400",
              boxShadow: "0 0 0 1px #3182CE",
              color: "blue.400",
              backgroundColor: "blue.50",
            }}
          >
            <option value="">{t("All Statuses")}</option>
            <option value="accepted">{t("Accepted")}</option>
            <option value="ready_to_collect">{t("Ready")}</option>
            <option value="pending">{t("Pending")}</option>
            <option value="completed">{t("Completed")}</option>
            <option value="cancelled">{t("Cancelled")}</option>
          </Select>
        </FormControl>
      )}

      {/* Orders Per Page */}
      {showOrdersPerPage && (
        <FormControl maxW={{ base: "100%", md: "120px" }}>
          <Select
            value={ordersPerPage}
            onChange={(e) => {
              setOrdersPerPage && setOrdersPerPage(parseInt(e.target.value, 10));
              setCurrentPage && setCurrentPage(1);
            }}
            focusBorderColor="blue.400"
            borderRadius="md"
            _hover={{
              borderColor: "blue.400",
              boxShadow: "0 0 0 1px #3182CE",
              color: "blue.400",
              backgroundColor: "blue.50",
            }}
          >
            <option value="10">10 {t("per page")}</option>
            <option value="20">20 {t("per page")}</option>
            <option value="50">50 {t("per page")}</option>
            <option value="100">100 {t("per page")}</option>
          </Select>
        </FormControl>
      )}

      {/* Reset Button */}
      <Button
        leftIcon={<MdOutlineFilterAltOff />}
        variant="outline"
        colorScheme="gray"
        size="md"
        onClick={handleReset}
        _hover={{
          borderColor: "red.500",
          color: "red.500",
          backgroundColor: "rgba(245, 101, 101, 0.1)",
          boxShadow: "0 0 0 2px #F56565",
        }}
      >
        {t("Reset")}
      </Button>
    </HStack>
  );
}
